import { computed, ref } from 'vue';

/**
 * The shape of the screen — ONE module owning the three media queries, so the
 * next card does not have to guess which breakpoint decides what (card
 * t_f6d03638):
 *
 *   - `isMd`       `(min-width: 768px)` — Tailwind's `md`: the line between the
 *                  phone chrome and the tablet/desktop one;
 *   - `isWide`     `(min-width: 1024px)` — the launcher's desktop side column.
 *                  Below it that column is not rendered at all and the phone
 *                  header owns the panel's data (card t_f64724d7);
 *   - `isPortrait` `(orientation: portrait)` — the shape that decides WHERE the
 *                  section navigation lives: a bar at the bottom edge in
 *                  portrait, the docked left column otherwise (card t_f6d03638).
 *
 * Module-level, so every consumer shares one query and one listener.
 */
const mdQuery =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(min-width: 768px)')
    : null;
const wideQuery =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(min-width: 1024px)')
    : null;
const portraitQuery =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(orientation: portrait)')
    : null;

const isMd = ref(mdQuery?.matches ?? false);
const isWide = ref(wideQuery?.matches ?? false);
const isPortrait = ref(portraitQuery?.matches ?? false);

function sync(): void {
  isMd.value = mdQuery?.matches ?? false;
  isWide.value = wideQuery?.matches ?? false;
  isPortrait.value = portraitQuery?.matches ?? false;
}

if (typeof window !== 'undefined') {
  window.addEventListener('resize', sync);
  window.addEventListener('orientationchange', sync);
  [mdQuery, wideQuery, portraitQuery].forEach((query) => query?.addEventListener('change', sync));
}

/** Which of the three shapes the shell is showing. */
export type NavPlacement = 'sidebar' | 'bottom' | 'drawer';

/**
 * Where the section navigation lives for a given shape:
 *
 *   - portrait                     → `'bottom'`  — phones AND tablets: the section
 *     navigation leaves the top band and becomes a bar at the bottom edge
 *     (the owner's feedback: "in portrait the navigation could be at the
 *     bottom");
 *   - landscape / desktop (≥768px) → `'sidebar'` — the docked left column,
 *     exactly as it has always been;
 *   - landscape, below md          → `'drawer'`  — a phone on its side keeps the
 *     off-canvas drawer (a bottom bar on a 390px-tall screen would eat the page).
 *
 * Pure and exported, so `useWideViewport.test.ts` pins the decision itself
 * instead of leaving it to screenshots.
 */
export function navPlacementFor(shape: { isMd: boolean; isPortrait: boolean }): NavPlacement {
  if (shape.isPortrait) return 'bottom';
  return shape.isMd ? 'sidebar' : 'drawer';
}

/**
 * How a *section's own* navigation rail behaves for a given shape — the rooms
 * list, the settings tree, a service menu. It is NOT the section navigation
 * above, and it is decided FROM it, so the two can never disagree (card
 * t_d9971280):
 *
 *   - the shell docks the section navigation in a left column (`'sidebar'`) →
 *     the rail is a COLUMN beside the content, exactly as before;
 *   - the shell keeps its navigation at the bottom edge (`'bottom'`, portrait)
 *     or in a drawer (`'drawer'`, phone landscape) → the rail is the off-canvas
 *     DRAWER the header's toggle opens. One navigation surface at a time: a
 *     224px column beside a bottom bar took 28% of an 800px portrait tablet
 *     (measured `0,57 224x1221` on the tablet, rooms page) and the toggle in the
 *     header then fought the column instead of opening anything.
 */
export type SubNavPlacement = 'column' | 'drawer';

export function subNavPlacementFor(shape: { isMd: boolean; isPortrait: boolean }): SubNavPlacement {
  return navPlacementFor(shape) === 'sidebar' ? 'column' : 'drawer';
}

/**
 * Where a *page-level* rail lives — the launcher shell's service menu, the
 * provider console's section menu. Same rule as the section's own rail above,
 * with one extra state: these rails are not on the page at all below `md`, where
 * the page carries a phone-sized alternate navigation instead (the services chip
 * row, the console's chip row). A toggle for a rail that does not exist is a
 * second way to reach a menu that is already reachable, so `'none'` is a real
 * answer and the shells read it (card t_8f690929: /services docked a 224px
 * column at 800x1333 portrait beside the bottom bar, leaving `main` 576 of 800px
 * while the bar's Services tab already opened the same list).
 */
export type RailPlacement = 'column' | 'drawer' | 'none';

export function railPlacementFor(shape: { isMd: boolean; isPortrait: boolean }): RailPlacement {
  if (!shape.isMd) return 'none';
  return subNavPlacementFor(shape);
}

/** The raw shape flags (any consumer that needs its own rule reads these). */
export function useWideViewport() {
  return { isWide, isMd, isPortrait };
}

/** Where the section navigation lives on this device shape. */
export function useNavPlacement() {
  return computed<NavPlacement>(() => navPlacementFor({ isMd: isMd.value, isPortrait: isPortrait.value }));
}

/** How a section's own rail (rooms, settings) behaves on this device shape. */
export function useSubNavPlacement() {
  return computed<SubNavPlacement>(() => subNavPlacementFor({ isMd: isMd.value, isPortrait: isPortrait.value }));
}

/** How a page-level rail (the service menu, the console's section menu) behaves. */
export function useRailPlacement() {
  return computed<RailPlacement>(() => railPlacementFor({ isMd: isMd.value, isPortrait: isPortrait.value }));
}

/**
 * The shell's questions, in the shell's own words (DefaultLayout,
 * LauncherSidebar and every launcher-shell view ask these instead of
 * re-deriving them from a query of their own).
 */
export function useBottomNavMode() {
  const placement = useNavPlacement();
  const subNavPlacement = useSubNavPlacement();
  return {
    placement,
    /** Portrait: the section navigation is a bar at the bottom edge. */
    isBottomNav: computed(() => placement.value === 'bottom'),
    /** Phones keep the off-canvas drawer beside the bar (the Work group, …). */
    hasDrawer: computed(() => !isMd.value),
    /** A section rail is docked beside the content only where the shell docks. */
    subNavPlacement,
    isSubNavColumn: computed(() => subNavPlacement.value === 'column'),
  };
}
